import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

//** COMPONENT */
import { Box, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { notiError } from "../../../component/notification/notifications";

//** API */
import RepairApi, { RepairAdditionalItem, RepairFileUpload } from "../../../api/repair/repair.api";

//** UTILS */
import { colors } from "../../../constants/colors";
import { dateToView } from "../../../utils/date.utils";
import { getApiErrorMessage } from "../../../utils/api-error.utils";

//** STYLE */
import { Boxfilearray } from "../add/addRepairStyle";

type Props = {
  repairId?: number;
  /** ใช้ handlepreview ของหน้าแม่ — แยกเคส PDF (เปิด tab) กับรูป (สลับ preview ใน modal) ให้แล้ว */
  onPreviewFile: (file: any) => void;
};

/**
 * รายการ "เรื่องที่แจ้งเพิ่มเติม" แบบอ่านอย่างเดียว สำหรับแทรกใน modal ADD_DOCUMENT / RECEIVE_APPROVE
 * ต้องเป็นลูกของ <Row> เพราะ return <Col> — modal ทั้งสองตัวห่อ body ด้วย <Row> ชั้นนอกอยู่
 * fetch เองตอน mount: modal ถูก conditional render ด้วย typeAction อยู่แล้ว
 * mount/unmount จึงตรงกับจังหวะเปิด/ปิด และได้ข้อมูลใหม่ทุกครั้งที่เปิด
 */
export default function RepairAdditionalSection({ repairId, onPreviewFile }: Props) {
  const [items, setItems] = useState<RepairAdditionalItem[]>([]);
  /** การ์ดที่กางอยู่ — key เป็น repairAdditionalId, เริ่มเป็น {} คือหุบทั้งหมด */
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (key: string) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!repairId) return;

    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadAdditional = async () => {
      try {
        const res = await RepairApi.additionalList(repairId);
        if (!isMounted) return;
        const rows = Array.isArray(res.data) ? [...res.data] : [];
        // เรียงเก่า -> ใหม่ ให้เลข "ครั้งที่ N" ตรงกับลำดับการแจ้งจริง ไม่พึ่ง order ที่ backend ส่งมา
        rows.sort(
          (a, b) => new Date(a.createDate || 0).getTime() - new Date(b.createDate || 0).getTime()
        );
        setItems(rows);
      } catch (error) {
        if (!isMounted) return;
        setItems([]);
        notiError(getApiErrorMessage(error, "ดึงรายการเรื่องที่แจ้งเพิ่มเติมไม่สำเร็จ"));
      }
    };

    loadAdditional();
    return () => {
      isMounted = false;
    };
  }, [repairId]);

  // ไม่มีรายการ = ไม่ต้องขึ้นอะไรเลย (รวมถึงตอนกำลังโหลด กัน หัวข้อกะพริบแล้วหาย)
  if (items.length === 0) return null;

  const renderFileRow = (file: RepairFileUpload, index: number) => (
    <Row key={file.fileId ?? `file-${index}`}>
      <Col lg={3} xs={12} className="my-2 d-flex justify-content-end align-items-center">
        {`ลำดับที่ : `}&nbsp;{index + 1}
      </Col>
      <Col lg={9} xs={12} className="my-2">
        <Boxfilearray>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ wordBreak: "break-all" }}>{file.fileName || "-"}</Box>
            <Box>
              <VisibilityIcon
                onClick={() => onPreviewFile(file)}
                sx={{ cursor: "pointer", marginRight: "10px" }}
              />
            </Box>
          </Box>
        </Boxfilearray>
      </Col>
    </Row>
  );

  const renderItem = (item: RepairAdditionalItem, index: number) => {
    const key = String(item.repairAdditionalId ?? index);
    const isExpanded = !!expandedRows[key];

    return (
      <Boxfilearray key={key} sx={{ mt: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: colors.themeMainColor,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {`แจ้งเรื่องเพิ่มเติมครั้งที่ ${index + 1}`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {/* dateToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ */}
            <Typography
              sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}
            >
              {item.createDate ? dateToView(item.createDate) : "-"}
            </Typography>
            <Tooltip title={isExpanded ? "ซ่อนรายละเอียด" : "ดูรายละเอียด"}>
              <IconButton
                size="small"
                onClick={() => toggleRow(key)}
                sx={{ color: colors.themeMainColor }}
              >
                {isExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* unmountOnExit เพื่อไม่ให้เนื้อหาที่ยังไม่ได้กางค้างอยู่ใน DOM */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {/* ใช้ borderTop ไม่ใช้ <Divider/> เพราะ custom.css:117-121 บังคับ
              margin ซ้าย/ขวา 16px + สีเข้ม ด้วย !important ทับ sx ไม่ได้ */}
          <Box sx={{ borderTop: `1px solid ${colors.lightGray}`, mt: 1.5, pt: 1.5 }} />
          <Row>
            <Col lg={3} xs={12} className="my-2">
              {"เรื่องที่แจ้งเพิ่ม :"}
            </Col>
            <Col lg={9} xs={12} className="my-2">
              <Typography sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                {item.description || "-"}
              </Typography>
            </Col>

            {(item.fileUpload?.length || 0) > 0 && (
              <>
                <Col lg={3} xs={12} className="my-2">
                  {"ไฟล์แนบ"}
                </Col>
                <Col lg={9} xs={12} className="my-2">
                  {item.fileUpload?.map(renderFileRow)}
                </Col>
              </>
            )}
          </Row>
        </Collapse>
      </Boxfilearray>
    );
  };

  return (
    <Col lg={12} xs={12} className="my-2">
      <Box sx={{ borderTop: `1px solid ${colors.lightGray}`, mt: 2, pt: 2 }} />
      <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>เรื่องที่แจ้งเพิ่มเติม</Typography>
      {items.map(renderItem)}
    </Col>
  );
}
